import { useCallback, useEffect, useRef, useState } from "react";
import Axios from "axios";
import { environment } from "../../environment";
import { errorCatcher } from "../../hooks";
import { useLogin } from "../login";
import { IDesignHistory, IDesignSearchResult } from "./types";

// #region useDesignHistory
/**
 * Admin-only (SUPERADMIN / SUPERUSER):
 *   GET /api/v2/design/history-search?q=   title search across all brands
 *   GET /api/v2/design/:id/history         full timeline for one design
 */
export const useDesignHistory = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');
    const [history, setHistory] = useState<IDesignHistory | null>(null);
    const [searching, setSearching] = useState(false);
    const [results, setResults] = useState<IDesignSearchResult[] | null>(null);
    const [, authToken, loginFunctions] = useLogin();
    const loginFunctionsRef = useRef(loginFunctions);

    useEffect(() => {
        loginFunctionsRef.current = loginFunctions;
    }, [loginFunctions]);

    const search = useCallback(async (term: string): Promise<IDesignSearchResult[] | null> => {
        const q = term.trim();
        if (q.length < 2) { setResults(null); return null; }
        setSearching(true);
        setError('');
        try {
            const response = await Axios.get<IDesignSearchResult[]>(`${environment.v2Url}/design/history-search`, {
                params: { q, limit: 100 },
                headers: { 'X-Wallmates-Auth': authToken }
            });
            setResults(response.data);
            return response.data;
        } catch (err: any) {
            errorCatcher(err, loginFunctionsRef.current.logout);
            setError(err?.response?.data?.message || err?.message || 'Design search failed.');
            setResults(null);
            return null;
        } finally {
            setSearching(false);
        }
    }, [authToken]);

    const load = useCallback(async (designId: number): Promise<IDesignHistory | null> => {
        setLoading(true);
        setError('');
        try {
            const response = await Axios.get<IDesignHistory>(`${environment.v2Url}/design/${designId}/history`, {
                headers: { 'X-Wallmates-Auth': authToken }
            });
            setHistory(response.data);
            return response.data;
        } catch (err: any) {
            errorCatcher(err, loginFunctionsRef.current.logout);
            const message = err?.response?.data?.message || err?.message || 'Failed to load design history.';
            setError(message);
            setHistory(null);
            return null;
        } finally {
            setLoading(false);
        }
    }, [authToken]);

    const clearResults = useCallback(() => setResults(null), []);

    return { loading, error, history, load, searching, results, search, clearResults };
};
